import { FetchError } from 'ofetch';

export type ReturnCodes
	= 'EmailAlreadyExists'
		| 'AccountNotFound'
		| 'ForceLogout'
		| 'VerifyItsYouNotConfirmed'
		| 'RecoveryEmailExists'
		| 'NoValidFcmTokens'
		| 'FailedDeliveringMobileNotification'
		| 'PasswordChangeRequired'
		| 'CannotSetPromoCode'
		| 'RecaptchaVerificationFailed'
		| 'CurrentlyUnavailable'
		| 'AlreadyDefined'
		| 'UserLockedOut'
		| 'LastLoginAttemptBeforeLockout'
		| 'VerifyItsYouTooManyFailedAttempts'
		| 'OtpCodeInvalid'
		| 'OtpCodeExpiredOrWrongState';

export const returnCodeMessages: Partial<Record<ReturnCodes, string>> = {
	NoValidFcmTokens: 'To proceed, please log in to the Hahnemühle mobile app first.',
	EmailAlreadyExists: 'This e-mail is already associated with another account. Please enter different e-mail.',
	FailedDeliveringMobileNotification: 'Set up of in-app prompt failed to start. Please, try again.',
	CannotSetPromoCode: 'Discount code cannot be applied!',
	RecaptchaVerificationFailed: 'Recaptcha verification failed. Please try to refresh the page and submit again. [ERR_RC_FAIL]',
	CurrentlyUnavailable: 'The service is currently unavailable. Please try again later.',
	AlreadyDefined: 'The entry you are trying to add already exists.',
	UserLockedOut: 'Your Hahnemühle ID has been locked for 5 minutes due to too many failed login attempts.',
	VerifyItsYouTooManyFailedAttempts: 'Verification is temporarily disabled for your Hahnemühle ID. Please try again in 1 hour.',
	OtpCodeInvalid: 'Wrong code. Please use the most recently received code.',
	OtpCodeExpiredOrWrongState: 'Code expired. Please choose Resend code to receive a new one.',
};

const httpStatusMessages: Record<number, string> = {
	403: "You don't have permission to do that.",
	404: "We couldn't find what you were looking for.",
	429: 'Blocked due to too many requests. Please try again in 5 minutes.',
	500: 'Something went wrong on our end. Please try again shortly.',
	502: 'Something went wrong on our end. Please try again shortly.',
	503: 'The service is temporarily unavailable. Please try again shortly.',
};

export function isOfetchError(error: any): error is FetchError {
	return error instanceof FetchError;
}

export function extractErrorCode<T = ReturnCodes>(error: any, checkOFetchInstance: boolean = true): T | undefined {
	if (checkOFetchInstance && isOfetchError(error)) {
		return error.data?.returnCode as T;
	}
	else if (!checkOFetchInstance && error && error.data && error.data.returnCode) {
		return error.data.returnCode as T;
	}

	return undefined;
}

/**
 * `ForceLogout` means the session was invalidated server-side — an admin blacklisted it, or the user
 * ran "log out from all devices". It arrives as a 403 both from the portals' blacklist filter and
 * from the token exchange, and no token refresh can rescue it: the app has to run its logout flow.
 */
export function isForceLogout(error: any): boolean {
	return (extractErrorCode(error) ?? extractErrorCode(error, false)) === 'ForceLogout';
}

export function isTimeoutError(error: any): boolean {
	return error?.cause?.name === 'TimeoutError' || error?.name === 'TimeoutError';
}

export function isNetworkError(error: any): boolean {
	if (typeof navigator !== 'undefined' && navigator.onLine === false) {
		return true;
	}
	// ofetch wraps fetch failures (offline, DNS, connection refused) with no response/status
	return isOfetchError(error) && !error.response && error.statusCode === undefined;
}

export function hasApiErrorData(error: any): boolean {
	return Boolean((isOfetchError(error) && error.data) || error?.data?.returnCode);
}

export function extractValidationMessage(error: any): string | undefined {
	const errors = error.data?.errors || error.data?.validationErrors;
	if (errors) {
		const errorValues = Object.values(errors) as string[][];
		return errorValues?.at(0)?.at(0);
	}
	return undefined;
}

export function getApiErrorMessage(error: any): string | undefined {
	const returnCode = error.data?.returnCode as ReturnCodes;

	// Check for known return code messages
	if (returnCode && returnCodeMessages[returnCode]) {
		return returnCodeMessages[returnCode];
	}

	// Check for HTTP status code messages
	if (error.statusCode && httpStatusMessages[error.statusCode]) {
		return httpStatusMessages[error.statusCode];
	}

	// Handle validation errors (400)
	if (error.statusCode === 400) {
		return extractValidationMessage(error) || error.data?.returnMsg;
	}

	// Fallback to returnMsg from response
	return error.data?.returnMsg;
}
